import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { ChevronDown, BarChart3, User, LogOut, Sparkles, FileText } from "lucide-react";
import "./Header.css";

function Header() {
  const { showAuthModal, user, logout } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [dropdownImageLoadError, setDropdownImageLoadError] = useState(false);
  const [logoLoadError, setLogoLoadError] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const logoSrc = "/logo-s.png";

  // REMOVED: Auto-redirect logic - users stay on current page after signing in

  // Function to get user initials
  const getUserInitials = (email) => {
    if (!email) return "U";
    const name = email.split("@")[0];
    return name.charAt(0).toUpperCase();
  };

  // Handle sign out
  const handleSignOut = () => {
    logout();
    setShowDropdown(false);
  };

  // Toggle dropdown
  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  // Get profile image URL
  const getProfileImageUrl = () => {
    return user?.photoURL || null;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Reset image load errors when profile image changes
  useEffect(() => {
    setImageLoadError(false);
    setDropdownImageLoadError(false);
  }, [user?.photoURL]);

  // Hide header on /generate page
  if (location.pathname === "/generate") return null;

  const profileImageUrl = getProfileImageUrl();
  const shouldShowImage = profileImageUrl && profileImageUrl.trim() !== "" && !imageLoadError;
  const shouldShowDropdownImage = profileImageUrl && profileImageUrl.trim() !== "" && !dropdownImageLoadError;

  return (
    <header className="header header-default light">
      {/* Logo Section */}
      <Link to="/" className="logo-container">
        {!logoLoadError ? (
          <>
            <img 
              src={logoSrc}
              alt="Cayoty Logo" 
              className="header-logo"
              onError={() => setLogoLoadError(true)}
              onLoad={() => setLogoLoadError(false)}
            />
            <h1 className="logo-text">Cayoty</h1>
          </>
        ) : (
          <h1 className="title">Cayoty</h1>
        )}
      </Link>

      {/* Navigation Links */}
      <nav className="header-nav">
        <Link to="/resources-help" className="nav-link">
          Resources
        </Link>
        <Link to="/pricing-plans" className="nav-link">
          Pricing
        </Link>
        <a 
          onClick={(e) => {
            e.preventDefault();
            if (user) {
              window.location.href = "/generate";
            } else {
              showAuthModal('signup');
            }
          }}
          className="nav-link"
          style={{ cursor: 'pointer' }}
        >
          Try Free
        </a>
      </nav>

      {/* Right Side - Auth/User Section */}
      <div className="button-group">
        {user ? (
          <div className="user-section" ref={dropdownRef}>
            <div className="user-info">
              <div className="profile-dropdown">
                <button 
                  className="profile-button" 
                  onClick={toggleDropdown}
                  aria-expanded={showDropdown}
                  aria-haspopup="true"
                  aria-label="User menu"
                >
                  <div className="profile-avatar">
                    {shouldShowImage ? (
                      <img 
                        src={profileImageUrl}
                        alt="Profile" 
                        className="profile-image"
                        onError={() => setImageLoadError(true)}
                        onLoad={() => setImageLoadError(false)}
                      />
                    ) : (
                      <span className="profile-initials">
                        {getUserInitials(user.email)}
                      </span>
                    )}
                  </div>
                  <ChevronDown 
                    className={`dropdown-arrow ${showDropdown ? "open" : ""}`}
                    size={12}
                  />
                </button>
                
                {showDropdown && (
                  <div className="dropdown-menu">
                    {/* Dropdown Header with User Info */}
                    <div className="dropdown-header">
                      <div className="dropdown-avatar">
                        {shouldShowDropdownImage ? (
                          <img 
                            src={profileImageUrl} 
                            alt="Profile" 
                            className="dropdown-profile-image"
                            onError={() => setDropdownImageLoadError(true)}
                            onLoad={() => setDropdownImageLoadError(false)}
                          />
                        ) : (
                          <span className="dropdown-profile-initials">
                            {getUserInitials(user.email)}
                          </span>
                        )}
                      </div>
                      <div className="dropdown-user-info">
                        <div className="dropdown-email">{user.email}</div>
                        
                      </div>
                    </div>
                    
                    <div className="dropdown-divider"></div>
                    
                    {/* Dropdown Menu Items */}
                    <div className="dropdown-items">
                      <Link 
                        to="/generate" 
                        className="dropdown-item-header" 
                        onClick={() => setShowDropdown(false)}
                      >
                        <Sparkles className="dropdown-icon-header" size={16} />
                        Create Project
                      </Link>
                      
                      <Link 
                        to="/resources-help" 
                        className="dropdown-item-header"
                        onClick={() => setShowDropdown(false)}
                      >
                        <BarChart3 className="dropdown-icon-header" size={16} />
                        Resources
                      </Link>
                      
                      <Link 
                        to="/pricing-plans" 
                        className="dropdown-item-header" 
                        onClick={() => setShowDropdown(false)}
                      >
                        <FileText className="dropdown-icon-header" size={16} />
                       Upgrade Plan
                      </Link>
                      
                      <Link 
                        to="/profile" 
                        className="dropdown-item-header"
                        onClick={() => setShowDropdown(false)}
                      >
                        <User className="dropdown-icon-header" size={16} />
                        Profile
                      </Link>
                    </div>
                    
                    <div className="dropdown-divider"></div>
                    
                    {/* Logout Button */}
                    <button 
                      className="dropdown-item-header logout-item"
                      onClick={handleSignOut}
                    >
                      <LogOut className="dropdown-icon-header" size={16} />
                      Log Out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <>
            <button
              className="btn-outline-header"
              onClick={() => showAuthModal("login")}
            >
              Log In
            </button>
            <button
              className="btn-primary-header"
              onClick={() => showAuthModal("signup")}
            >
              Sign Up for Free
            </button>
          </>
        )}
      </div>
    </header>
  );
}

export default Header;